import type { VerenaProof } from '../core/config';

interface VerenaIframeConfig {
  apiUrl: string;
  sessionId: string;
  theme: string;
  onVerified: (proof: VerenaProof) => void;
  onError: (error: Error) => void;
}

export class VerenaIframe {
  private iframe: HTMLIFrameElement | null = null;
  private frame: HTMLElement | null = null;
  private popup: Window | null = null;
  private messageListener: ((e: MessageEvent) => void) | null = null;
  private loadTimeout: ReturnType<typeof setTimeout> | null = null;
  private apiOrigin: string;
  private config: VerenaIframeConfig;

  constructor(config: VerenaIframeConfig) {
    this.config = config;
    this.apiOrigin = new URL(config.apiUrl).origin;
  }

  // The widget frame from the Verena design system: 380px max x 88px (a 1px border around the
  // iframe, which pads its 74px row by 6px itself so the progress edge can sit on the bottom
  // border). Drawn here in the host page so its shadow isn't clipped by the iframe. The height is
  // constant across states, so the host page never reflows.
  create(): HTMLElement {
    const frame = document.createElement('div');
    frame.className = 'verena-frame';
    Object.assign(frame.style, {
      boxSizing: 'border-box', width: '100%', maxWidth: '380px', height: '88px', padding: '0',
      borderRadius: '10px', borderWidth: '1px', borderStyle: 'solid', overflow: 'hidden',
    });
    const paint = () => {
      const dark = this.config.theme === 'dark'
        || (this.config.theme === 'auto' && matchMedia('(prefers-color-scheme: dark)').matches);
      frame.style.background = dark ? '#24211f' : '#ffffff';
      frame.style.borderColor = dark ? '#3a3633' : '#e7e5e4';
      frame.style.boxShadow = dark ? '0 4px 16px rgba(0,0,0,0.35)' : '0 4px 16px rgba(26,24,22,0.06)';
      if (this.iframe) this.iframe.style.colorScheme = dark ? 'dark' : 'light';
    };

    const iframe = document.createElement('iframe');
    iframe.src = `${this.config.apiUrl}widget?session=${this.config.sessionId}&mode=iframe&theme=${this.config.theme}`;
    iframe.allow = 'publickey-credentials-get; publickey-credentials-create';
    iframe.title = 'Verena human verification';
    iframe.style.border = 'none';
    iframe.style.width = '100%';
    iframe.style.height = '86px';
    iframe.style.overflow = 'hidden';
    iframe.style.display = 'block';
    this.iframe = iframe;
    this.frame = frame;
    paint();
    if (this.config.theme === 'auto') matchMedia('(prefers-color-scheme: dark)').addEventListener('change', paint);
    frame.appendChild(iframe);

    this.messageListener = (e: MessageEvent) => this.handleMessage(e);
    window.addEventListener('message', this.messageListener);

    this.loadTimeout = setTimeout(() => {
      this.config.onError(new Error('Widget failed to load'));
    }, 15000);

    return frame;
  }

  private handleMessage(e: MessageEvent): void {
    if (e.origin !== this.apiOrigin) return;
    const { type, data } = e.data || {};

    switch (type) {
      case 'verena:ready':
        if (this.loadTimeout) {
          clearTimeout(this.loadTimeout);
          this.loadTimeout = null;
        }
        break;
      case 'verena:verified':
        this.config.onVerified(data.proof);
        // Forward to iframe so it shows success state (relevant when popup completed)
        if (this.popup && this.iframe?.contentWindow) {
          this.iframe.contentWindow.postMessage(
            { type: 'verena:popup-result', data: { proof: data.proof } },
            this.apiOrigin,
          );
        }
        break;
      case 'verena:error':
        this.config.onError(new Error(data.error));
        break;
      case 'verena:need-popup':
        this.openPopup(data.sessionId);
        break;
    }
  }

  openPopup(sessionId?: string): void {
    const sid = sessionId || this.config.sessionId;
    const url = `${this.config.apiUrl}widget?session=${sid}&mode=popup&theme=${this.config.theme}&action=create`;
    this.popup = window.open(url, 'verena-popup', 'width=420,height=520,popup=yes');
    if (!this.popup) {
      this.config.onError(new Error('Popup blocked. Please allow popups for verification.'));
    }
  }

  destroy(): void {
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
      this.messageListener = null;
    }
    if (this.loadTimeout) {
      clearTimeout(this.loadTimeout);
      this.loadTimeout = null;
    }
    if (this.frame) {
      this.frame.remove();
      this.frame = null;
      this.iframe = null;
    }
    if (this.popup && !this.popup.closed) {
      this.popup.close();
      this.popup = null;
    }
  }
}
